import { FormField, Label, StepperInput, type StepperInputProps } from "@mdotm/shadcn/mdotm/components";
import { FormController } from "$root/third-party-integrations/react-hook-form";
import { valueByPath } from "$root/utils";
import type { StylableProps } from "@mdotm/shadcn/legacy/components";
import { type NodeOrFn, renderNodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import type { Control, FieldValues, FormState, Path } from "react-hook-form";

export type StepperInputFieldProps<T extends FieldValues> = StylableProps & {
	control: Control<T, any>;
	formState: FormState<T>;
	name: Path<T>;
	label: NodeOrFn<{ htmlFor: string }>;
	/**
	 * Let the field be emptied back to "no value": blurring it blank writes `null`. Only for fields whose
	 * form value is nullable and where "unanswered" means something the stepper's minimum cannot express.
	 */
	clearable?: boolean;
} & Omit<StepperInputProps, "value" | "onChange" | "onClear">;

export function StepperInputField<T extends FieldValues>({
	label,
	name,
	formState,
	control,
	className,
	style,
	clearable,
	...forward
}: StepperInputFieldProps<T>): React.JSX.Element {
	return (
		<FormField
			className={className}
			style={style}
			label={({ htmlFor }) =>
				typeof label === "string" ? (
					<Label htmlFor={htmlFor} className="mb-px">
						{label}
					</Label>
				) : (
					renderNodeOrFn(label, { htmlFor })
				)
			}
			error={(valueByPath(formState.errors, name) as { message?: string })?.message}
		>
			{(formFieldProps) => (
				<FormController
					control={control}
					name={name}
					render={({ field: { value, onChange } }) => (
						<StepperInput
							value={value ?? null}
							onChange={onChange}
							onClear={clearable ? () => onChange(null) : undefined}
							{...forward}
							{...formFieldProps}
						/>
					)}
				/>
			)}
		</FormField>
	);
}
